import {
  safeParseBrandingDefinition,
  type BrandingDefinition,
} from "@lemn-ltd/brand-contract";
import {
  systemBrandingTemplates,
} from "@lemn-ltd/brand-contract/system-brandings";
import {
  BrandStudio,
  type BrandStudioHostStatus,
  type BrandStudioIntent,
} from "@lemn-ltd/brand-studio";
import { Alert } from "@lemn-ltd/ui";
import { useEffect, useRef, useState } from "react";
import type {
  BrandingVersionDto,
  PreviewTargetDto,
} from "../../api/generated.ts";
import {
  ControlPlaneApiError,
  controlPlaneApi,
} from "../../api/client.ts";
import { patchEditableRoots } from "./branding-patch.ts";

type Notice = {
  readonly tone: "success" | "error";
  readonly message: string;
};

export function GovernedBrandStudio({
  workspaceId,
  version,
  previewTargets,
  onRefresh,
  onNotice,
  onDirtyChange,
  onPreviewChange,
  onRequestSystemBrandingDraft,
  onRequestPreview,
  readOnly = false,
}: {
  readonly workspaceId: string;
  readonly version: BrandingVersionDto;
  readonly previewTargets: readonly PreviewTargetDto[];
  readonly onRefresh: () => Promise<void>;
  readonly onNotice: (notice: Notice) => void;
  readonly onDirtyChange: (dirty: boolean) => void;
  readonly onPreviewChange: (preview: {
    readonly versionId: string;
    readonly definition: BrandingDefinition;
    readonly modeId: string;
  } | undefined) => void;
  readonly onRequestSystemBrandingDraft: (request: {
    readonly templateId: string;
    readonly templateVersion: number;
  }) => void;
  readonly onRequestPreview: (request: {
    readonly brandingVersionId: string;
    readonly targetId: string;
    readonly initialModeId: string;
  }) => void;
  /** Presentation guard for deployments that deliberately expose the
   * dashboard as a development-only, public read-only demonstration. The
   * server authorization boundary remains authoritative. */
  readonly readOnly?: boolean;
}) {
  const initial = parseDefinition(version.definition);
  const [definition, setDefinition] = useState<BrandingDefinition | undefined>(
    initial.definition,
  );
  const [incompatibleMessage, setIncompatibleMessage] = useState(
    initial.error,
  );
  const [persistedIdentity, setPersistedIdentity] = useState(
    versionIdentity(version),
  );
  const [hostStatus, setHostStatus] = useState<BrandStudioHostStatus>(() =>
    statusForVersion(version),
  );
  const [dirty, setDirty] = useState(false);
  const [modeId, setModeId] = useState(initial.definition?.defaultModeId ?? "");
  const operationInFlight = useRef(false);

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  useEffect(() => () => onPreviewChange(undefined), [onPreviewChange]);

  useEffect(() => {
    if (!definition) {
      onPreviewChange(undefined);
      return;
    }
    const resolvedModeId = definition.modes[modeId] ? modeId : definition.defaultModeId;
    if (resolvedModeId !== modeId) setModeId(resolvedModeId);
    onPreviewChange({ versionId: version.id, definition, modeId: resolvedModeId });
  }, [definition, modeId, onPreviewChange, version.id]);

  useEffect(() => {
    const identity = versionIdentity(version);
    if (identity === persistedIdentity) return;
    const parsed = parseDefinition(version.definition);
    setDefinition(parsed.definition);
    setIncompatibleMessage(parsed.error);
    setModeId(parsed.definition?.defaultModeId ?? "");
    setPersistedIdentity(identity);
    setDirty(false);
    setHostStatus(
      parsed.error
        ? { state: "invalid", message: parsed.error }
        : statusForVersion(version),
    );
  }, [persistedIdentity, version]);

  if (!definition) {
    return (
      <Alert
        variant="error"
        title="Branding definition is incompatible"
        message={
          incompatibleMessage ??
          "This version cannot be opened with the pinned BrandingDefinition contract."
        }
      />
    );
  }

  const runIntent = async (intent: BrandStudioIntent): Promise<void> => {
    if (readOnly) {
      setHostStatus({
        state: "permission-denied",
        message: "This development demonstration is read-only.",
      });
      return;
    }
    if (operationInFlight.current) {
      setHostStatus({
        state: "error",
        message: "Wait for the current governed operation to finish.",
      });
      return;
    }

    try {
      assertIntentVersion(intent, version);

      if (intent.type === "select-system-branding") {
        if (dirty) {
          setHostStatus({
            state: "dirty",
            message: "Save the current draft before opening another system branding.",
          });
          return;
        }
        const template = systemBrandingTemplates.find(
          (entry) =>
            entry.id === intent.templateId &&
            entry.version === intent.templateVersion &&
            entry.definitionHash === intent.definitionHash &&
            entry.status === "available",
        );
        if (!template) {
          throw new Error(
            "The selected System branding is unavailable in the pinned catalog.",
          );
        }
        setHostStatus({
          state: "saved",
          message: `Confirm the new ${template.id}@${template.version} draft in the dialog.`,
        });
        onRequestSystemBrandingDraft({
          templateId: template.id,
          templateVersion: template.version,
        });
        return;
      }

      if (intent.type === "compare-draft") {
        const unchanged = intent.definitionHash === version.definitionHash;
        setDirty(!unchanged);
        setHostStatus({
          state: unchanged ? "saved" : "dirty",
          message: unchanged
            ? "The local compiler hash matches the persisted draft."
            : `Local ${shortHash(intent.definitionHash)} differs from persisted ${shortHash(version.definitionHash)}.`,
        });
        return;
      }

      if (intent.type === "validate") {
        if (!intent.compileResult.ok) {
          setHostStatus({
            state: "invalid",
            message: `${intent.compileResult.diagnostics.filter((entry) => entry.severity === "error").length} blocking diagnostics must be resolved.`,
          });
          return;
        }
        if (intent.compileResult.artifact.definitionHash !== version.definitionHash) {
          setDirty(true);
          setHostStatus({
            state: "dirty",
            message:
              "Local compilation passed. Save the draft before governed server validation.",
          });
          return;
        }
        operationInFlight.current = true;
        setHostStatus({ state: "validating", message: "Validating persisted draft…" });
        await controlPlaneApi.validateVersion(
          workspaceId,
          version.id,
          version.definitionHash,
        );
        setHostStatus({
          state: "saved",
          message: "The persisted draft passed governed validation.",
        });
        onNotice({ tone: "success", message: "Draft validation passed." });
        return;
      }

      operationInFlight.current = true;
      if (intent.type === "save-draft") {
        setHostStatus({ state: "saving", message: "Saving with hash concurrency…" });
        const operations = patchEditableRoots(
          version.definition,
          intent.definition,
        );
        if (operations.length === 0) {
          setDirty(false);
          setHostStatus({ state: "saved", message: "The draft is already current." });
          return;
        }
        const saved = await controlPlaneApi.patchVersion(
          workspaceId,
          version.id,
          {
            expectedDefinitionHash: version.definitionHash,
            idempotencyKey: intent.idempotencyKey,
            operations,
          },
        );
        const parsed = parseDefinition(saved.definition);
        if (!parsed.definition) throw new Error(parsed.error);
        setDefinition(parsed.definition);
        setDirty(false);
        setHostStatus({
          state: "saved",
          message: `Draft saved at ${shortHash(saved.definitionHash)}.`,
        });
        onNotice({
          tone: "success",
          message: "Draft saved with optimistic hash concurrency.",
        });
        await onRefresh();
        return;
      }

      if (intent.type === "create-preview") {
        if (intent.definitionHash !== version.definitionHash) {
          throw new UnsavedPreviewError();
        }
        setHostStatus({
          state: "saved",
          message: "Confirm the exact hash-bound preview in the dialog.",
        });
        onRequestPreview({
          brandingVersionId: version.id,
          targetId: intent.targetId,
          initialModeId: intent.initialModeId,
        });
        return;
      }

      if (
        dirty &&
        (intent.type === "archive-draft" || intent.type === "publish-draft")
      ) {
        setHostStatus({
          state: "dirty",
          message: "Save the current draft before this governed operation.",
        });
        return;
      }

      const mutationInput = {
        expectedDefinitionHash: version.definitionHash,
        idempotencyKey: intent.idempotencyKey,
      };
      if (intent.type === "archive-draft") {
        setHostStatus({ state: "saving", message: "Archiving draft…" });
        await controlPlaneApi.archiveVersion(workspaceId, version.id, mutationInput);
        setHostStatus({ state: "archived", message: "Draft archived." });
      } else if (intent.type === "restore-draft") {
        setHostStatus({ state: "saving", message: "Restoring draft…" });
        await controlPlaneApi.restoreVersion(workspaceId, version.id, mutationInput);
        setHostStatus({ state: "saved", message: "Draft restored." });
      } else {
        setHostStatus({
          state: "publishing",
          message: "Publication was accepted and is materializing asynchronously.",
        });
        await controlPlaneApi.publishVersion(workspaceId, version.id, mutationInput);
      }
      onNotice({ tone: "success", message: "The governed operation completed." });
      await onRefresh();
    } catch (error) {
      const status = statusForError(error);
      setHostStatus(status);
      onNotice({ tone: "error", message: status.message ?? "The operation failed." });
    } finally {
      operationInFlight.current = false;
    }
  };

  return (
    <>
      <BrandStudio
      value={definition}
      draft={{
        brandingVersionId: version.id,
        title: version.title,
        definitionHash: version.definitionHash,
        state: version.state,
        archived: version.archivedAt !== null,
      }}
      hostStatus={hostStatus}
      onChange={(next) => {
        if (readOnly || version.state !== "draft" || version.archivedAt !== null) return;
        setDefinition(next);
        setDirty(true);
        setHostStatus({
          state: "dirty",
          message: "Local changes are not yet persisted.",
        });
      }}
      onIntent={runIntent}
      modeId={modeId}
      onModeIdChange={setModeId}
      previewPlacement="external"
      previewTargets={previewTargets.map((target) => ({
        id: target.id,
        name: target.name,
        origin: target.origin,
        status: target.status,
      }))}
      readOnly={readOnly || version.state !== "draft" || version.archivedAt !== null}
      systemBrandings={systemBrandingTemplates}
      />
    </>
  );
}

function parseDefinition(input: unknown): {
  readonly definition?: BrandingDefinition;
  readonly error?: string;
} {
  const parsed = safeParseBrandingDefinition(input);
  if (parsed.success) return { definition: parsed.data };
  return {
    error: parsed.error.issues
      .slice(0, 5)
      .map(
        (issue) =>
          `${issue.path.join(".") || "$"}: ${issue.message}`,
      )
      .join("; "),
  };
}

function assertIntentVersion(
  intent: BrandStudioIntent,
  version: BrandingVersionDto,
): void {
  if (
    "brandingVersionId" in intent &&
    intent.brandingVersionId !== version.id
  ) {
    throw new Error("The Studio intent belongs to another BrandingVersion.");
  }
  if (
    "expectedDefinitionHash" in intent &&
    intent.expectedDefinitionHash !== version.definitionHash
  ) {
    throw new Error("The Studio intent carries a stale persisted hash.");
  }
}

function statusForVersion(version: BrandingVersionDto): BrandStudioHostStatus {
  if (version.archivedAt) return { state: "archived", message: "Draft archived." };
  if (version.state === "publishing") {
    return { state: "publishing", message: "Publication is materializing." };
  }
  if (version.state === "published") {
    return { state: "published", message: "This published version is immutable." };
  }
  return { state: "saved", message: "Draft matches the persisted definition hash." };
}

function versionIdentity(version: BrandingVersionDto): string {
  return [
    version.id,
    version.definitionHash,
    version.state,
    version.archivedAt ?? "active-draft",
  ].join(":");
}

function statusForError(error: unknown): BrandStudioHostStatus {
  if (error instanceof UnsavedPreviewError) {
    return { state: "dirty", message: error.message };
  }
  if (error instanceof ControlPlaneApiError) {
    const state =
      error.status === 403
        ? "permission-denied"
        : error.status === 409
          ? "conflict"
          : error.status === 410
            ? "expired"
            : "error";
    return {
      state,
      message: `${error.message} (${error.code ?? error.status})`,
    };
  }
  return {
    state: "error",
    message: error instanceof Error ? error.message : "The operation failed.",
  };
}

class UnsavedPreviewError extends Error {
  public constructor() {
    super("Save the draft before creating an exact preview.");
  }
}

function shortHash(value: string): string {
  return value.slice(0, 12);
}
